// File: standalone
// Created: 2026-05-29
// Updated: 2026-05-29
// Author: Erick Hernández Silva

import React from 'react';
import { createRoot } from 'react-dom/client';
import CarConfigurator from './components/CarConfigurator';
import CarAdminFormNew from './components/CarAdminFormNew';
import { IS_PREVIEW } from './config.js';
import './styles/tailwind.css';

// Preview builds (dev stage) get a visible marker so nobody mistakes them for prod.
function showPreviewBadge() {
  if (!IS_PREVIEW || document.getElementById('cfg-preview-badge')) return;
  const badge = document.createElement('div');
  badge.id = 'cfg-preview-badge';
  badge.textContent = 'PREVIEW · dev data';
  badge.style.cssText =
    'position:fixed;left:50%;top:0;transform:translateX(-50%);z-index:2147483647;' +
    'background:#f59e0b;color:#111;font:600 11px/1 system-ui,sans-serif;letter-spacing:.06em;' +
    'padding:5px 10px;border-radius:0 0 6px 6px;pointer-events:none;';
  document.body.appendChild(badge);
}

// Capture this script's own URL so we can load the matching CSS next to it.
const SELF_SRC = (function () {
  try {
    if (document.currentScript && document.currentScript.src) return document.currentScript.src;
  } catch {}
  const s = document.querySelector('script[src*="vehicle-configurator"]');
  return (s && s.src) || null;
})();

(function () {
  const cssHref = SELF_SRC ? SELF_SRC.replace(/\.js(\?.*)?$/, '.css$1') : null;

  // Mount React inside a Shadow DOM so the host page's CSS (e.g. Webflow's
  // global styles) cannot leak in, and our styles stay fully contained.
  // Our stylesheet is loaded INTO the shadow root via <link>. If we can't
  // resolve the CSS URL, fall back to a normal (light DOM) mount.
  function mount(container, element) {
    if (cssHref) {
      let shadow = container.shadowRoot;
      if (!shadow) shadow = container.attachShadow({ mode: 'open' });
      shadow.replaceChildren();

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssHref;
      shadow.appendChild(link);

      // Base styling inside the shadow root (Tailwind's html/body rules don't
      // reach in). Sets a sane default font and block display for the host.
      const base = document.createElement('style');
      base.textContent =
        ':host{display:block;}' +
        '.cfg-root{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:#1e293b;}';
      shadow.appendChild(base);

      const mountEl = document.createElement('div');
      mountEl.className = 'cfg-root';
      shadow.appendChild(mountEl);
      createRoot(mountEl).render(element);
      return;
    }
    createRoot(container).render(element);
  }

  // The host page (Webflow) has a near-black body. On mobile Safari that color
  // fills the areas above/below the page (status bar, toolbar, overscroll), so
  // the configurator page is switched to white.
  function useWhitePage() {
    document.documentElement.style.setProperty('background-color', '#fff', 'important');
    document.body.style.setProperty('background-color', '#fff', 'important');
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', '#ffffff');
  }

  window.initCarConfigurator = function (containerId = 'root') {
    const container = document.getElementById(containerId);
    if (container) {
      useWhitePage();
      mount(container, React.createElement(CarConfigurator, { containerId }));
    } else {
      console.error(`Container with ID '${containerId}' not found`);
    }
  };

  window.initCarAdmin = function (containerId = 'root') {
    const container = document.getElementById(containerId);
    if (container) {
      mount(container, React.createElement(CarAdminFormNew));
    } else {
      console.error(`Container with ID '${containerId}' not found`);
    }
  };

  // Auto-initialize based on container attributes
  function autoInit() {
    showPreviewBadge();
    const rootContainer = document.getElementById('root');
    if (!rootContainer) {
      console.log('❌ No container with id="root" found');
      return;
    }
    const showAdmin = rootContainer.hasAttribute('show-admin');
    const showConfig = rootContainer.hasAttribute('show-config');

    if (showAdmin) {
      window.initCarAdmin('root');
    } else if (showConfig) {
      // With data-car-record → that car; without → the "Select model" splash.
      window.initCarConfigurator('root');
    } else {
      console.log('⚠️ No valid attributes found (need show-admin or show-config)');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
})();
